
import React, { useMemo, useState, useEffect } from 'react';
import { Box, useMediaQuery, Drawer, IconButton, CircularProgress } from '@mui/material';
import CustomText, { textStyles } from '../../../../components/CustomText';
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import StatusDropdown from "../../../../components/StatusDropdown";
import profilecross from "../../../../assets/profilecross.svg";
import { decryptToken } from "../../../../Utlis/Encryption";
import { BASE_URL } from "../../../../components/BaseUrl";
import { useTheme } from "../../../../contexts/ThemeContext";
import { convertUTCToLocalDateTime } from "../../../../components/dateTimeUtils";
import { GoogleMap, Marker, Circle, InfoWindow } from "@react-google-maps/api";

const fullDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const mapContainerStyle = { width: "100%", height: "100%" };
const markerIconUrl =
  "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png";

const getMarkerIcon = () => {
  if (!window.google?.maps) return { url: markerIconUrl };
  return {
    url: markerIconUrl,
    scaledSize: new window.google.maps.Size(25, 41),
    anchor: new window.google.maps.Point(12, 41),
  };
};

const formatWorkingHoursDisplay = (dailyHours) => {
  if (dailyHours === null || dailyHours === undefined || dailyHours === "" || dailyHours === "-") {
    return "—";
  }
  const hours = Number(dailyHours);
  if (Number.isNaN(hours)) return dailyHours;

  const totalMinutes = Math.max(0, Math.round(hours * 60));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  if (h > 0 && m > 0) return `${h}h ${m}mins`;
  if (h > 0) return `${h}h`;
  return `${m}mins`;
};

/** Same UTC->local conversion as the worker Attendance Settings module (AttendanceSettings.js). */
const formatScheduleWallTime = (timeValue) => {
  const utcValue = timeValue || "-";
  return convertUTCToLocalDateTime(utcValue, {
    formatOptions: { hour: "numeric", minute: "2-digit", hour12: true },
  });
};

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
    <CustomText sx={{ ...textStyles.h1, fontSize: 16, fontWeight: 400, color: "#5E5C5C", textAlign: "left" }}>
      {label}
    </CustomText>
    <CustomText sx={{ ...textStyles.h1, fontSize: 15, fontWeight: 400, color: "#172B4D", textAlign: "right" }}>
      {value ?? "—"}
    </CustomText>
  </Box>
);

const DepartmentDetails = ({ department, onBack, open, onClose }) => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const { t } = useTranslation();
  const { primaryColor } = useTheme();

  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isMapPopupOpen, setIsMapPopupOpen] = useState(false);

  useEffect(() => {
    const fetchDetail = async () => {
      if (!open || !department?.id || !decryptedToken) return;
      setLoading(true);
      setDetail(null);
      try {
        const url = `${BASE_URL}/departments/${department.id}`;
        console.log("📡 [DepartmentDetails] Fetching:", url);
        const res = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${decryptedToken}`,
            "Content-Type": "application/json",
          },
        });
        const json = await res.json();
        console.log("📊 [DepartmentDetails] Response:", json);
        if (!json?.error && json?.data?.department) {
          setDetail(json.data.department);
          console.log("🕒 [DepartmentDetails] Raw schedule from API:", json.data.department?.attendanceSettings?.schedule, "| Browser timezone:", Intl.DateTimeFormat().resolvedOptions().timeZone);
        }
      } catch (err) {
        console.error("Error fetching department detail:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [open, department?.id, decryptedToken]);

  const current = detail || department || {};
  const attendanceSettings = detail?.attendanceSettings;
  const schedule = attendanceSettings?.schedule;
  const location = attendanceSettings?.location;
  const hasSettings = Boolean(attendanceSettings?.hasSettings);

  const formattedCreated = useMemo(() => {
    if (!current?.created_at) return "—";
    return new Date(current.created_at)
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .replace(",", " -");
  }, [current?.created_at]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 450,
          maxWidth: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box sx={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#E7EBEE", p: 1.3, borderBottom: "1px solid #E0E0E0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.2 }}>
          <IconButton onClick={onClose}>
            <img src={profilecross} alt="Back" />
          </IconButton>
          <CustomText sx={{ ...textStyles.h1, fontSize: 20, fontWeight: 600, color: "#2C384C" }}>
            {t("departmentDetails.heading")}
          </CustomText>
        </Box>
        {loading && <CircularProgress size={18} sx={{ color: primaryColor, mr: 1 }} />}
      </Box>

      {/* Content */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, overflowY: "auto" }}>
        <Box sx={{ border: "1px solid #E0E0E0", borderRadius: 2, p: 2 }}>
          <CustomText sx={{ ...textStyles.h1, fontSize: 18, mb: 3, mt: 1, fontWeight: 600, color: '#003149', textAlign: 'left' }}>
            {t("departmentDetails.heading")}
          </CustomText>

          <DetailRow label={t("departmentDetails.id")} value={current?.id} />

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <CustomText sx={{ ...textStyles.h1, fontSize: 16, fontWeight: 400, color: "#5E5C5C", textAlign: "left" }}>
              {t("departmentDetails.status")}
            </CustomText>
            <Box sx={{ pointerEvents: "none" }}>
              <StatusDropdown currentStatus={current?.is_active ? "Active" : "Inactive"} />
            </Box>
          </Box>

          <DetailRow label={t("departmentDetails.departmentName")} value={current?.name} />
          <DetailRow label={t("departmentDetails.companyId", "Company ID")} value={current?.company_id} />
          <DetailRow label={t("departmentDetails.employeeCount", "Employees")} value={current?.worker_count ?? current?.workers?.length ?? 0} />
          <DetailRow label={t("departmentDetails.createdOn", "Created On")} value={formattedCreated} />
        </Box>

        {/* Schedule */}
        <Box sx={{ border: "1px solid #E0E0E0", borderRadius: 2, p: 2 }}>
          <CustomText sx={{ ...textStyles.h1, fontSize: 18, mb: 2, fontWeight: 600, color: '#003149', textAlign: 'left' }}>
            {t("departmentDetails.scheduleHeading", "Schedule")}
          </CustomText>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={22} sx={{ color: primaryColor }} />
            </Box>
          ) : !hasSettings ? (
            <CustomText sx={{ ...textStyles.h1, fontSize: 14, fontWeight: 400, color: "#8C8C8C", textAlign: "left" }}>
              {t("departmentDetails.noSchedule", "No attendance schedule configured for this department.")}
            </CustomText>
          ) : (
            <>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3, gap: 1, flexWrap: "wrap" }}>
                {fullDays.map((day) => {
                  const short = day.slice(0, 3);
                  const isSelected = Array.isArray(schedule?.days) && schedule.days.includes(short);
                  return (
                    <Box
                      key={day}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "13px",
                        fontWeight: 600,
                        userSelect: "none",
                        bgcolor: isSelected ? primaryColor : "#e0e0e0",
                        color: isSelected ? "#fff" : "#000",
                      }}
                    >
                      {day[0]}
                    </Box>
                  );
                })}
              </Box>

              <DetailRow label={t("editModal.labels.shiftStart")} value={formatScheduleWallTime(schedule?.start)} />
              <DetailRow label={t("editModal.labels.shiftEnd")} value={formatScheduleWallTime(schedule?.end)} />
              <DetailRow label={t("editModal.labels.graceMinutes")} value={schedule?.graceMinutes ?? "—"} />
              <DetailRow label={t("editModal.labels.breakMinutes")} value={schedule?.breakPolicy?.unpaid ?? "—"} />
              <DetailRow label={t("departmentDetails.dailyHours", "Daily Working Hours")} value={formatWorkingHoursDisplay(schedule?.dailyHours)} />
            </>
          )}
        </Box>

        {/* Location */}
        {hasSettings && location && (
          <Box sx={{ border: "1px solid #E0E0E0", borderRadius: 2, p: 2 }}>
            <CustomText sx={{ ...textStyles.h1, fontSize: 18, mb: 2, fontWeight: 600, color: '#003149', textAlign: 'left' }}>
              {t("departmentDetails.locationHeading", "Location")}
            </CustomText>
            <DetailRow label={t("editModal.labels.locationName")} value={location?.locationName} />
            <DetailRow label={t("editModal.labels.locationRadius")} value={location?.radiusMeters} />

            {location?.lat != null && location?.lng != null && (
              <Box sx={{ height: 220, borderRadius: "8px", overflow: "hidden", mt: 1 }}>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={{ lat: Number(location.lat), lng: Number(location.lng) }}
                  zoom={15}
                  options={{
                    fullscreenControl: false,
                    mapTypeControl: false,
                    streetViewControl: false,
                  }}
                >
                  <Marker
                    position={{ lat: Number(location.lat), lng: Number(location.lng) }}
                    icon={getMarkerIcon()}
                    onClick={() => setIsMapPopupOpen(true)}
                  />
                  {isMapPopupOpen && (
                    <InfoWindow
                      position={{ lat: Number(location.lat), lng: Number(location.lng) }}
                      onCloseClick={() => setIsMapPopupOpen(false)}
                    >
                      <Box sx={{ fontFamily: "Poppins, sans-serif" }}>
                        {location?.locationName || "Work Location"}
                      </Box>
                    </InfoWindow>
                  )}
                  <Circle
                    center={{ lat: Number(location.lat), lng: Number(location.lng) }}
                    radius={Number(location.radiusMeters) || 100}
                    options={{
                      strokeColor: "#1976d2",
                      strokeOpacity: 1,
                      strokeWeight: 1,
                      fillColor: "#1976d2",
                      fillOpacity: 0.1,
                    }}
                  />
                </GoogleMap>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default DepartmentDetails;
